import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Outlet, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material';
import _merge from 'lodash/merge';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { Schema } from 'yup';

import {
  AuthConfigDTO,
  getGetOrganizationNormsQueryKey,
  IdValidationConfig,
  IdValidationConfigPreset,
  OrganizationNormsResponse,
  OrganizationUpdateRequestDTO,
  OrganizationUpdateRequestDTOTalentUniqueFieldsItem,
  ProcessSettings,
  TalentQuestionsResponseDTO,
  useGetOrganizationBySlug,
  useGetOrganizationNorms,
  useGetOrganizationTalentQuestions,
  useUpdateOrganization,
  useUpdateOrganizationNorms
} from '@/api/generated';
import BackdropLoader from '@/components/BackdropLoader';
import ErrorGeneric from '@/components/ErrorGeneric/ErrorGeneric';
import { LocationProvider } from '@/hooks/entities/useLocation';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useOrganizationPermissions } from '@/hooks/permissions/useOrganizationPermissions';
import {
  orgSettingsTabs,
  PROCESS_SETTINGS_STEP_KEYS,
  ROUTER_PATHS,
  SETTINGS_STEP_KEYS,
  toOrganizationSettings
} from '@/root/linker';
import {
  accessInvitationMappingPresave,
  createAccessInvitationDefaultValues,
  createTalentRegistrationDefaultValues,
  createTestPropertiesDefaultValues,
  testSettingsMappingPresave
} from '@/views/ProcessSettingsForm/defaults';
import SettingsShell from '@/views/SettingsShell/SettingsShell';

import { deserializeNorms, serializeNorms } from './norms';
import {
  getBasicInfoValidationSchema,
  getRolesMembersValidationSchema,
  getTalentRegistrationValidationSchema,
  getTestsProcessesValidationSchema
} from './schema';
import {
  ID_STRUCTURE_OPTIONS,
  type OrganizationData,
  type OrganizationSettingsFieldValues,
  type OrganizationSettingsFieldValuesAuthConfig,
  OrganizationSettingsFieldValuesIdValidationConfig
} from './types';

const DEFAULT_ORGANIZATION_VALUES: Partial<OrganizationSettingsFieldValues> = {
  testValidityDays: 180
};

const ORGANIZATION_KEYS_BY_TAB = {
  [SETTINGS_STEP_KEYS.BASIC_INFO]: [
    'isEnterprise',
    'status',
    'name',
    'legalName',
    'industry',
    'offices',
    'countries',
    'keyAccountManagers',
    'employeeCount',
    'accountNumbers',
    'anonymizationPolicy',
    'anonymizationDurationMonths',
    'numberOfEmployees',
    'jiraId',
    'legacyId',
    'pipedriveId'
  ],
  [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: ['admins', 'userInviteEmailLanguage', 'authConfig'],
  [SETTINGS_STEP_KEYS.PROCESS_SETTINGS]: ['processSettings'],
  [SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS]: ['testValidityDays', 'idValidation', 'talentUniqueFields', 'norms']
} as { [key: string]: string[] };

const ALL_ORGANIZATION_KEYS = Object.values(ORGANIZATION_KEYS_BY_TAB).flatMap(list => list);
const OMIT_ON_SAVE_KEYS = ['isEnterprise', 'status', 'norms'];

const TRANSFORMED_ON_LOAD_KEYS = [
  'admins',
  'industry',
  'keyAccountManagers',
  'idValidation',
  'processSettings',
  'authConfig',
  'talentUniqueFields',
  'norms'
];

const MFA_METHODS = ['app', 'passkey', 'whatsapp', 'sms', 'call'] as (keyof AuthConfigDTO)[];

const presaveTransform = (fieldValues: OrganizationSettingsFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
  const originalAuthConfig = fieldValues?.authConfig;
  const originalIdValidation = fieldValues?.idValidation;
  // TODO: Apply this strcture to backend side to avoid so many transformations.

  const authConfigTransformed = {
    invalidateAfterDays: originalAuthConfig?.invalidateAfterDays,
    ...MFA_METHODS.reduce(
      (acc, key) => ({
        ...acc,
        [key]: originalAuthConfig?.methods?.includes(key)
      }),
      {}
    )
  } satisfies AuthConfigDTO;

  const processSettings = _merge(
    accessInvitationMappingPresave(fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION]),
    testSettingsMappingPresave(fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES])
  ) satisfies ProcessSettings;

  const isLetters = originalIdValidation?.structure === ID_STRUCTURE_OPTIONS.LETTERS;
  const isDigits = originalIdValidation?.structure === ID_STRUCTURE_OPTIONS.NUMBERS;
  const isBoth = originalIdValidation?.structure === ID_STRUCTURE_OPTIONS.BOTH;

  // TODO: Apply this strcture to backend side to avoid so many transformations.
  const idValidationTransformed: IdValidationConfig | undefined =
    (originalIdValidation?.preset &&
      originalIdValidation?.preset !== IdValidationConfigPreset.NONE && {
        preset: originalIdValidation?.preset,
        maxLength: +(originalIdValidation?.maxLength || 0),
        allowLetters: isLetters || isBoth,
        allowDigits: isDigits || isBoth
      }) ||
    undefined;

  const updateRequest = _pick(fieldValues, ALL_ORGANIZATION_KEYS) as OrganizationUpdateRequestDTO;

  // Admins are already UserIdentifier objects - pass through as-is
  updateRequest.admins = fieldValues?.admins;
  updateRequest.authConfig = authConfigTransformed;
  updateRequest.processSettings = processSettings;
  updateRequest.idValidation = idValidationTransformed;
  updateRequest.talentUniqueFields = fieldValues.talentUniqueFields ? [fieldValues.talentUniqueFields] : [];

  return _omit(_pick(updateRequest, ORGANIZATION_KEYS_BY_TAB[stepKey]), OMIT_ON_SAVE_KEYS);
};

const preloadTransform = (
  organization: OrganizationData,
  organizationNormsData: OrganizationNormsResponse | undefined,
  talentQuestions: TalentQuestionsResponseDTO
) => {
  const accessInvitationData = createAccessInvitationDefaultValues(organization.processSettings!);
  const testPropertiesData = createTestPropertiesDefaultValues(organization.processSettings!);

  const fieldValues = _omit(
    _pick(organization, ALL_ORGANIZATION_KEYS),
    TRANSFORMED_ON_LOAD_KEYS
  ) as OrganizationSettingsFieldValues;

  const { allowLetters, allowDigits } = organization?.idValidation || {};

  const structure: ID_STRUCTURE_OPTIONS | null = [
    allowLetters && !allowDigits && ID_STRUCTURE_OPTIONS.LETTERS,
    !allowLetters && allowDigits && ID_STRUCTURE_OPTIONS.NUMBERS,
    allowLetters && allowDigits && ID_STRUCTURE_OPTIONS.BOTH
  ].filter((item): item is ID_STRUCTURE_OPTIONS => Boolean(item))[0];

  // TODO: Simplify structure of the data to avoid so many transformations.
  fieldValues.idValidation = {
    preset: organization?.idValidation?.preset,
    maxLength: organization?.idValidation?.maxLength,
    structure
  } satisfies OrganizationSettingsFieldValuesIdValidationConfig;

  fieldValues.authConfig = {
    invalidateAfterDays: organization.authConfig?.invalidateAfterDays || 1,
    methods: Object.entries(_omit(organization.authConfig, 'invalidateAfterDays'))
      .map(([key, value]) => value && key)
      .filter((item): item is string => Boolean(item))
  } satisfies OrganizationSettingsFieldValuesAuthConfig;

  // TODO: Normalize the naming and structure of the fields.
  fieldValues.industry = organization?.industry?.id;
  fieldValues.keyAccountManagers = organization?.keyAccountManagers?.filter(user => user?.id)?.map(user => user.id!);
  fieldValues.admins = organization?.admins?.filter(admin => admin?.id)?.map(({ id }) => ({ id: id! }));
  fieldValues.talentUniqueFields =
    organization?.talentUniqueFields?.[0] ?? OrganizationUpdateRequestDTOTalentUniqueFieldsItem.ID_NUMBER;
  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_ACCESS_INVITATION] = accessInvitationData;
  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TEST_PROPERTIES] = testPropertiesData;
  fieldValues.testValidityDays = fieldValues?.testValidityDays || DEFAULT_ORGANIZATION_VALUES?.testValidityDays;

  const deserializedNorms = deserializeNorms(organizationNormsData);
  if (deserializedNorms) {
    fieldValues.norms = deserializedNorms;
  }

  fieldValues[PROCESS_SETTINGS_STEP_KEYS.SETTINGS_TALENT_REGISTRATION] =
    createTalentRegistrationDefaultValues(talentQuestions);

  return fieldValues;
};

const OrganizationSettings: FC = () => {
  const { spacing } = useTheme();
  const { t } = useTranslation(['settings', 'common', 'validation']);

  const { organizationSlug } = useParams() as { organizationSlug: string };

  const {
    value: { canViewProcessSettings, canViewIdentificationAndNorms, canViewBasicInfo, canViewRolesAndUsers } = {},
    isLoading: isPermissionLoading
  } = useOrganizationPermissions({
    variables: { organizationSlug }
  });

  const { isMidotUser } = useIsMidotUser();

  const {
    data: organizationData,
    isLoading: isOrganizationLoading,
    isError: isOrganizationError,
    refetch
  } = useGetOrganizationBySlug(organizationSlug, { isFull: true });
  const {
    data: organizationNormsData,
    isLoading: isOrganizationNormsLoading,
    isError: isOrganizationNormsError
  } = useGetOrganizationNorms(organizationSlug);
  const { mutateAsync: updateOrganization } = useUpdateOrganization();
  const { mutateAsync: updateOrganizationNorms } = useUpdateOrganizationNorms();
  const queryClient = useQueryClient();

  const organization = organizationData as OrganizationData;

  const saveOrganizationSettings = useCallback(
    async (data: OrganizationSettingsFieldValues, stepKey: SETTINGS_STEP_KEYS) => {
      const organizationNorms =
        stepKey === SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS && data.norms ? serializeNorms(data.norms) : null;

      const updatePromises = [
        updateOrganization({
          organizationSlug,
          data: presaveTransform(data, stepKey)
        }),
        organizationNorms &&
          organizationNorms.length > 0 &&
          updateOrganizationNorms({
            organizationSlug,
            data: {
              organizationNorms
            }
          })
      ].filter(Boolean);

      await Promise.all(updatePromises);
      await refetch();
      if (organizationNorms) {
        await queryClient.invalidateQueries({
          queryKey: getGetOrganizationNormsQueryKey(organizationSlug)
        });
      }
    },
    [refetch, organizationSlug, updateOrganization, updateOrganizationNorms, queryClient]
  );

  const tabs = useMemo(
    () =>
      orgSettingsTabs(
        t,
        (stepKey: SETTINGS_STEP_KEYS) => toOrganizationSettings({ organizationSlug, stepKey }),
        canViewProcessSettings,
        canViewIdentificationAndNorms,
        canViewBasicInfo,
        canViewRolesAndUsers,
        isMidotUser
      ),
    [
      organizationSlug,
      t,
      canViewProcessSettings,
      canViewIdentificationAndNorms,
      canViewBasicInfo,
      canViewRolesAndUsers,
      isMidotUser
    ]
  );

  const {
    data: talentQuestions,
    isLoading: isTalentQuestionsLoading,
    isError: isTalentQuestionsError
  } = useGetOrganizationTalentQuestions<TalentQuestionsResponseDTO, object>(organizationSlug);

  const showBackdropLoader =
    isPermissionLoading || isOrganizationLoading || isOrganizationNormsLoading || isTalentQuestionsLoading;

  const hasBootstrapError = isOrganizationError || isOrganizationNormsError || isTalentQuestionsError;

  const defaultValues = useMemo(() => {
    if (!organization || talentQuestions === undefined) return undefined;

    return preloadTransform(organization, organizationNormsData, talentQuestions);
  }, [organization, organizationNormsData, talentQuestions]);

  const validations = useMemo(
    () =>
      ({
        [SETTINGS_STEP_KEYS.BASIC_INFO]: getBasicInfoValidationSchema(t),
        [SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS]: getRolesMembersValidationSchema(t),
        [SETTINGS_STEP_KEYS.PROCESS_SETTINGS]: getTestsProcessesValidationSchema(t),
        [SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS]: getTalentRegistrationValidationSchema(t)
      }) as { [key: string]: Schema<OrganizationSettingsFieldValues> },
    [t]
  );

  const defaultStepKey = useMemo(() => {
    if (canViewBasicInfo) return SETTINGS_STEP_KEYS.BASIC_INFO;
    if (canViewRolesAndUsers) return SETTINGS_STEP_KEYS.ROLES_AND_MEMBERS;
    if (canViewProcessSettings) return SETTINGS_STEP_KEYS.PROCESS_SETTINGS;
    if (canViewIdentificationAndNorms) return SETTINGS_STEP_KEYS.IDENTIFICATION_AND_NORMS;

    return SETTINGS_STEP_KEYS.BASIC_INFO;
  }, [canViewProcessSettings, canViewIdentificationAndNorms, canViewBasicInfo, canViewRolesAndUsers]);

  if (showBackdropLoader) {
    return <BackdropLoader />;
  }

  if (hasBootstrapError) {
    return <ErrorGeneric />;
  }

  return (
    <LocationProvider organizationSlug={organizationSlug} locationSlug={organizationSlug}>
      <SettingsShell
        tabs={tabs}
        defaultValues={defaultValues!}
        defaultRoute={toOrganizationSettings({ organizationSlug, stepKey: defaultStepKey })}
        routePattern={ROUTER_PATHS.ORGANIZATION_SETTINGS_GENERIC}
        sx={{ px: 5, pb: 5, margin: spacing(2, 1) }}
        onSave={saveOrganizationSettings}
        validations={validations}
      >
        <Outlet />
      </SettingsShell>
    </LocationProvider>
  );
};

export default OrganizationSettings;
